import { useState } from 'react'
import { useParams, useNavigate, Link } from 'react-router-dom'
import { usePlugin, useDeletePlugin, useUpdatePlugin, usePromoteVersion, type UpdatePluginData } from '../hooks/usePlugins'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Badge } from '@/components/ui/badge'
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Textarea } from '@/components/ui/textarea'
import { Switch } from '@/components/ui/switch'
import { Skeleton } from '@/components/ui/skeleton'
import { toast } from 'sonner'
import {
  ArrowLeft,
  ArrowUpRight,
  Download,
  Trash2,
  Lock,
  Unlock,
  Package,
  Calendar,
  User,
  Code,
  Globe,
  CheckCircle,
  Pencil,
  GitBranch,
} from 'lucide-react'
import { formatDistanceToNow, format } from 'date-fns'
import { InstalledOnTab } from '../components/InstalledOnTab'

export function PluginDetailPage() {
  const { slug } = useParams<{ slug: string }>()
  const navigate = useNavigate()
  const { data: plugin, isLoading, error } = usePlugin(slug || '')
  const deletePlugin = useDeletePlugin()
  const updatePlugin = useUpdatePlugin()
  const promoteVersion = usePromoteVersion()

  const [showDeleteDialog, setShowDeleteDialog] = useState(false)
  const [showEditDialog, setShowEditDialog] = useState(false)
  const [showPromoteDialog, setShowPromoteDialog] = useState(false)
  const [versionToPromote, setVersionToPromote] = useState<string | null>(null)
  const [editFormData, setEditFormData] = useState<Omit<UpdatePluginData, 'slug'>>({
    name: '',
    author: '',
    requires_php: '',
    requires_wp: '',
    tested_wp: '',
    requires_license: false,
    sections: {
      description: '',
      changelog: '',
    },
  })

  const handleDelete = async () => {
    if (!slug) return
    try {
      await deletePlugin.mutateAsync(slug)
      toast.success('Plugin deleted successfully')
      navigate('/dashboard/plugins')
    } catch (err) {
      toast.error('Failed to delete plugin')
    }
  }

  const openEditDialog = () => {
    if (!plugin) return
    setEditFormData({
      name: plugin.name || '',
      author: plugin.author || '',
      requires_php: plugin.requires_php || '',
      requires_wp: plugin.requires_wp || '',
      tested_wp: plugin.tested_wp || '',
      requires_license: plugin.requires_license || false,
      sections: {
        description: plugin.sections?.description || '',
        changelog: plugin.sections?.changelog || '',
      },
    })
    setShowEditDialog(true)
  }

  const handleUpdate = async () => {
    if (!slug) return
    try {
      await updatePlugin.mutateAsync({
        slug,
        ...editFormData,
      })
      toast.success('Plugin updated successfully')
      setShowEditDialog(false)
    } catch (err) {
      toast.error('Failed to update plugin')
    }
  }

  const handlePromote = async () => {
    if (!slug || !versionToPromote) return
    try {
      await promoteVersion.mutateAsync({
        slug,
        version: versionToPromote,
        toChannel: 'stable',
      })
      toast.success(`Version ${versionToPromote} promoted to stable`)
      setShowPromoteDialog(false)
      setVersionToPromote(null)
    } catch (err) {
      toast.error('Failed to promote version')
    }
  }

  const openPromoteDialog = (version: string) => {
    setVersionToPromote(version)
    setShowPromoteDialog(true)
  }

  if (error) {
    return (
      <div className="space-y-6">
        <Button variant="ghost" asChild>
          <Link to="/dashboard/plugins">
            <ArrowLeft className="mr-2 h-4 w-4" />
            Back to Plugins
          </Link>
        </Button>
        <Card>
          <CardContent className="p-6">
            <p className="text-destructive">Failed to load plugin: {String(error)}</p>
          </CardContent>
        </Card>
      </div>
    )
  }

  if (isLoading || !plugin) {
    return (
      <div className="space-y-6">
        <Skeleton className="h-8 w-32" />
        <div className="space-y-4">
          <Skeleton className="h-12 w-full" />
          <Skeleton className="h-48 w-full" />
          <Skeleton className="h-64 w-full" />
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div className="flex items-start justify-between">
        <div className="space-y-1">
          <Button variant="ghost" size="sm" className="-ml-2 mb-2" asChild>
            <Link to="/dashboard/plugins">
              <ArrowLeft className="mr-2 h-4 w-4" />
              Back to Plugins
            </Link>
          </Button>
          <div className="flex items-center gap-3">
            <h1 className="text-2xl font-bold">{plugin.name}</h1>
            <Badge variant="outline">{plugin.current_version}</Badge>
            <Badge variant="secondary">
              {plugin.type === 'theme' ? 'Theme' : 'Plugin'}
            </Badge>
            {plugin.requires_license ? (
              <Badge variant="outline" className="text-amber-600 border-amber-600">
                <Lock className="mr-1 h-3 w-3" />
                License Required
              </Badge>
            ) : (
              <Badge variant="outline" className="text-green-600 border-green-600">
                <Unlock className="mr-1 h-3 w-3" />
                Free
              </Badge>
            )}
          </div>
          <p className="text-muted-foreground">{plugin.slug}</p>
        </div>
        <div className="flex items-center gap-2">
          <Button variant="outline" onClick={openEditDialog}>
            <Pencil className="mr-2 h-4 w-4" />
            Edit Metadata
          </Button>
          <Button variant="outline" asChild>
            <a
              href={`/api/update-server/plugins/${plugin.slug}/download?version=${plugin.current_version}`}
              download
              onClick={() => toast.success('Download started')}
            >
              <Download className="mr-2 h-4 w-4" />
              Download Latest
            </a>
          </Button>
          <Button
            variant="destructive"
            onClick={() => setShowDeleteDialog(true)}
          >
            <Trash2 className="mr-2 h-4 w-4" />
            Delete Plugin
          </Button>
        </div>
      </div>

      {/* Metadata Card */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Plugin Information</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
            <MetadataItem
              icon={User}
              label="Author"
              value={plugin.author || 'Unknown'}
            />
            <MetadataItem
              icon={Code}
              label="Requires PHP"
              value={plugin.requires_php || 'Not specified'}
            />
            <MetadataItem
              icon={Globe}
              label="Requires WordPress"
              value={plugin.requires_wp || 'Not specified'}
            />
            <MetadataItem
              icon={CheckCircle}
              label="Tested up to"
              value={plugin.tested_wp || 'Not specified'}
            />
            <MetadataItem
              icon={Download}
              label="Total Downloads"
              value={String(plugin.total_downloads || 0)}
            />
            <MetadataItem
              icon={Calendar}
              label="Last Updated"
              value={
                plugin.last_updated
                  ? formatDistanceToNow(new Date(plugin.last_updated), { addSuffix: true })
                  : 'Never'
              }
            />
          </div>
        </CardContent>
      </Card>

      {/* Sections Tabs */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Details</CardTitle>
        </CardHeader>
        <CardContent>
          <Tabs defaultValue="description">
            <TabsList>
              <TabsTrigger value="description">Description</TabsTrigger>
              <TabsTrigger value="changelog">Changelog</TabsTrigger>
              <TabsTrigger value="installed-on">Installed on</TabsTrigger>
              {plugin.sections?.installation && (
                <TabsTrigger value="installation">Installation</TabsTrigger>
              )}
              {plugin.sections?.faq && (
                <TabsTrigger value="faq">FAQ</TabsTrigger>
              )}
            </TabsList>
            <TabsContent value="description" className="mt-4">
              <div className="prose prose-sm dark:prose-invert max-w-none">
                {plugin.sections?.description ? (
                  <div dangerouslySetInnerHTML={{ __html: plugin.sections.description }} />
                ) : (
                  <p className="text-muted-foreground">No description available.</p>
                )}
              </div>
            </TabsContent>
            <TabsContent value="changelog" className="mt-4">
              <div className="prose prose-sm dark:prose-invert max-w-none">
                {plugin.sections?.changelog ? (
                  <pre className="whitespace-pre-wrap font-mono text-sm bg-muted p-4 rounded-lg">
                    {plugin.sections.changelog}
                  </pre>
                ) : (
                  <p className="text-muted-foreground">No changelog available.</p>
                )}
              </div>
            </TabsContent>
            <TabsContent value="installed-on" className="mt-4">
              <InstalledOnTab slug={plugin.slug} pluginName={plugin.name} />
            </TabsContent>
            {plugin.sections?.installation && (
              <TabsContent value="installation" className="mt-4">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <div dangerouslySetInnerHTML={{ __html: plugin.sections.installation }} />
                </div>
              </TabsContent>
            )}
            {plugin.sections?.faq && (
              <TabsContent value="faq" className="mt-4">
                <div className="prose prose-sm dark:prose-invert max-w-none">
                  <div dangerouslySetInnerHTML={{ __html: plugin.sections.faq }} />
                </div>
              </TabsContent>
            )}
          </Tabs>
        </CardContent>
      </Card>

      {/* Release Channels */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg flex items-center gap-2">
            <GitBranch className="h-5 w-5" />
            Release Channels
          </CardTitle>
          <CardDescription>
            Manage stable and beta release channels
          </CardDescription>
        </CardHeader>
        <CardContent>
          <div className="grid gap-4 sm:grid-cols-2">
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border">
              <div>
                <div className="flex items-center gap-2">
                  <Badge variant="default">Stable</Badge>
                  <span className="text-sm text-muted-foreground">Production</span>
                </div>
                <p className="mt-1 font-mono text-lg font-semibold">
                  v{plugin.channels?.stable || plugin.current_version}
                </p>
              </div>
              <CheckCircle className="h-5 w-5 text-green-500" />
            </div>
            <div className="flex items-center justify-between p-4 rounded-lg bg-muted/50 border">
              <div>
                <div className="flex items-center gap-2">
                  <Badge variant="secondary">Beta</Badge>
                  <span className="text-sm text-muted-foreground">Testing</span>
                </div>
                <p className="mt-1 font-mono text-lg font-semibold">
                  {plugin.channels?.beta ? `v${plugin.channels.beta}` : 'No beta version'}
                </p>
              </div>
              {plugin.channels?.beta && (
                <Button
                  size="sm"
                  variant="outline"
                  onClick={() => openPromoteDialog(plugin.channels!.beta!)}
                >
                  <ArrowUpRight className="mr-1 h-4 w-4" />
                  Promote
                </Button>
              )}
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Versions Table */}
      <Card>
        <CardHeader>
          <CardTitle className="text-lg">Version History</CardTitle>
          <CardDescription>
            {Object.keys(plugin.versions || {}).length} version(s) available
          </CardDescription>
        </CardHeader>
        <CardContent>
          {Object.keys(plugin.versions || {}).length === 0 ? (
            <div className="flex flex-col items-center justify-center py-12 text-center">
              <Package className="h-12 w-12 text-muted-foreground mb-4" />
              <h3 className="text-lg font-medium">No versions uploaded</h3>
              <p className="text-muted-foreground">
                Upload a ZIP file to create the first version
              </p>
            </div>
          ) : (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Version</TableHead>
                  <TableHead>Channel</TableHead>
                  <TableHead>Release Date</TableHead>
                  <TableHead>Requirements</TableHead>
                  <TableHead>Downloads</TableHead>
                  <TableHead className="w-[140px]">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {Object.entries(plugin.versions)
                  .sort(([a], [b]) => b.localeCompare(a, undefined, { numeric: true }))
                  .map(([version, info]) => {
                    const isStable = version === (plugin.channels?.stable || plugin.current_version)
                    const isBeta = version === plugin.channels?.beta
                    return (
                      <TableRow key={version}>
                        <TableCell>
                          <div className="flex items-center gap-2">
                            <Badge
                              variant={isStable ? 'default' : 'outline'}
                            >
                              {version}
                            </Badge>
                          </div>
                        </TableCell>
                        <TableCell>
                          {isStable && (
                            <Badge variant="default" className="bg-green-600">stable</Badge>
                          )}
                          {isBeta && (
                            <Badge variant="secondary" className="bg-amber-500 text-white">beta</Badge>
                          )}
                          {!isStable && !isBeta && info.channel && (
                            <Badge variant="outline">{info.channel}</Badge>
                          )}
                        </TableCell>
                        <TableCell>
                          {info.release_date
                            ? format(new Date(info.release_date), 'MMM d, yyyy')
                            : 'Unknown'}
                        </TableCell>
                        <TableCell className="text-sm text-muted-foreground">
                          {info.requires_php && `PHP ${info.requires_php}`}
                          {info.requires_php && info.requires_wp && ' / '}
                          {info.requires_wp && `WP ${info.requires_wp}`}
                          {!info.requires_php && !info.requires_wp && 'Not specified'}
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center">
                            <Download className="mr-1 h-4 w-4 text-muted-foreground" />
                            {info.downloads || 0}
                          </div>
                        </TableCell>
                        <TableCell>
                          <div className="flex items-center gap-1">
                            <Button variant="ghost" size="icon" asChild>
                              <a
                                href={`/api/update-server/plugins/${plugin.slug}/download?version=${version}`}
                                title={`Download v${version}`}
                                onClick={() => toast.success('Download started')}
                              >
                                <Download className="h-4 w-4" />
                              </a>
                            </Button>
                            {isBeta && (
                              <Button
                                variant="ghost"
                                size="icon"
                                onClick={() => openPromoteDialog(version)}
                                title="Promote to stable"
                              >
                                <ArrowUpRight className="h-4 w-4" />
                              </Button>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    )
                  })}
              </TableBody>
            </Table>
          )}
        </CardContent>
      </Card>

      {/* Delete Confirmation */}
      <AlertDialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Delete Plugin</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to delete "{plugin.name}"? This will remove all versions
              and cannot be undone.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDelete}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              {deletePlugin.isPending ? 'Deleting...' : 'Delete'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Promote Confirmation */}
      <AlertDialog open={showPromoteDialog} onOpenChange={setShowPromoteDialog}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Promote to Stable</AlertDialogTitle>
            <AlertDialogDescription>
              This will make version {versionToPromote} available to all sites via auto-update.
              Sites configured for stable channel will receive this update.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction onClick={handlePromote}>
              {promoteVersion.isPending ? 'Promoting...' : 'Promote to Stable'}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Edit Metadata Dialog */}
      <Dialog open={showEditDialog} onOpenChange={setShowEditDialog}>
        <DialogContent className="max-w-2xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle>Edit Plugin Metadata</DialogTitle>
            <DialogDescription>
              Update the metadata for {plugin.name}. Changes will be reflected immediately.
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="grid gap-4 sm:grid-cols-2">
              <div className="space-y-2">
                <Label htmlFor="edit-name">Name</Label>
                <Input
                  id="edit-name"
                  value={editFormData.name}
                  onChange={(e) => setEditFormData({ ...editFormData, name: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-author">Author</Label>
                <Input
                  id="edit-author"
                  value={editFormData.author}
                  onChange={(e) => setEditFormData({ ...editFormData, author: e.target.value })}
                />
              </div>
            </div>

            <div className="grid gap-4 sm:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="edit-requires-php">Requires PHP</Label>
                <Input
                  id="edit-requires-php"
                  placeholder="7.4"
                  value={editFormData.requires_php}
                  onChange={(e) => setEditFormData({ ...editFormData, requires_php: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-requires-wp">Requires WordPress</Label>
                <Input
                  id="edit-requires-wp"
                  placeholder="5.0"
                  value={editFormData.requires_wp}
                  onChange={(e) => setEditFormData({ ...editFormData, requires_wp: e.target.value })}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="edit-tested-wp">Tested up to</Label>
                <Input
                  id="edit-tested-wp"
                  placeholder="6.4"
                  value={editFormData.tested_wp}
                  onChange={(e) => setEditFormData({ ...editFormData, tested_wp: e.target.value })}
                />
              </div>
            </div>

            <div className="flex items-center justify-between py-2">
              <div className="space-y-0.5">
                <Label>Requires License</Label>
                <p className="text-xs text-muted-foreground">
                  Private plugin requiring license key for updates
                </p>
              </div>
              <Switch
                checked={editFormData.requires_license}
                onCheckedChange={(checked) => setEditFormData({ ...editFormData, requires_license: checked })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-description">Description</Label>
              <Textarea
                id="edit-description"
                rows={4}
                placeholder="Plugin description (supports HTML)"
                value={editFormData.sections?.description || ''}
                onChange={(e) => setEditFormData({
                  ...editFormData,
                  sections: { ...editFormData.sections, description: e.target.value }
                })}
              />
            </div>

            <div className="space-y-2">
              <Label htmlFor="edit-changelog">Changelog</Label>
              <Textarea
                id="edit-changelog"
                rows={4}
                placeholder="Version changelog"
                value={editFormData.sections?.changelog || ''}
                onChange={(e) => setEditFormData({
                  ...editFormData,
                  sections: { ...editFormData.sections, changelog: e.target.value }
                })}
              />
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setShowEditDialog(false)}>
              Cancel
            </Button>
            <Button onClick={handleUpdate} disabled={updatePlugin.isPending}>
              {updatePlugin.isPending ? 'Saving...' : 'Save Changes'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  )
}

// Helper component for metadata items
function MetadataItem({
  icon: Icon,
  label,
  value,
}: {
  icon: React.ComponentType<{ className?: string }>
  label: string
  value: string
}) {
  return (
    <div className="flex items-center gap-3">
      <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-muted">
        <Icon className="h-5 w-5 text-muted-foreground" />
      </div>
      <div>
        <p className="text-sm text-muted-foreground">{label}</p>
        <p className="font-medium">{value}</p>
      </div>
    </div>
  )
}
